// ./src/utils/api/ExpenseService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { ExpenseTag } from "./ExpenseTagService";
import { ExpenseKind, RevenueKind } from "./AppEssentialsService";
import { Travel } from "./TravelService";
import { UploadedFile } from "./UploadedFileService";
import { TireSet } from "./TireSetService";

// =============================================================================
// GraphQL Field Fragments
// =============================================================================

/**
 * Core expense fields shared across all queries and mutations.
 * Includes all scalar fields, address, coordinates, and weather data.
 */
const EXPENSE_CORE_FIELDS = `
  id
  accountId
  userId
  carId
  expenseType
  odometer
  odometerDisplay
  tripMeter
  tripMeterDisplay
  whenDone
  location
  whereDone
  subtotal
  tax
  fees
  totalPrice
  paidInCurrency
  totalPriceInHc
  homeCurrency
  expensePictureId
  comments
  fuelInTank
  travelId
  ownerNumber
  kindId
  costWork
  costParts
  costWorkHc
  costPartsHc
  shortNote
  refuelVolume
  refuelVolumeDisplay
  volumeEnteredIn
  pricePerVolume
  isFullTank
  remainingInTankBefore
  fuelGrade
  tankType
  consumption
  pointType
  # Address fields
  address1
  address2
  city
  postalCode
  stateProvince
  country
  countryId
  savedPlaceId
  # Coordinates
  longitude
  latitude
  # Weather data
  weatherTempC
  weatherFeelsLikeC
  weatherConditionCode
  weatherConditionIcon
  weatherDescription
  weatherHumidityPct
  weatherPressureHpa
  weatherCloudPct
  weatherVisibilityM
  weatherWindSpeedMps
  weatherWindDirDeg
  weatherPrecipMm
  weatherUvIndex
  weatherProvider
  weatherFetchedAt
  tireSetId
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
`;

/**
 * Minimal relations: user, car, tags, tireSet.
 * Used by list queries where full detail isn't needed.
 */
const EXPENSE_LIST_RELATIONS = `
  user {
    id
    firstName
    lastName
    email
  }
  car {
    id
    label
  }
  expenseKind {
    id
    code
    name
  }
  revenueKind {
    id
    code
    name
  }
  tags {
    id
    tagName
    tagColor
  }
  tireSet {
    name
  }
`;

/**
 * Full relations: includes travel, audit users, uploaded files, and
 * extended expenseKind/revenueKind fields.
 * Used by get, getMany, create, and update queries.
 */
const EXPENSE_DETAIL_RELATIONS = `
  user {
    id
    firstName
    lastName
    email
  }
  car {
    id
    label
  }
  tags {
    id
    tagName
    tagColor
  }
  travel {
    id
    firstDttm
    lastDttm
    firstOdometer
    lastOdometer
  }
  expenseKind {
    id
    code
    name
    canSchedule
    isItMaintenance
  }
  revenueKind {
    id
    code
    name
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
  uploadedFiles {
    id
    sizeBytes
    name
    fileUrl
    thumbnailUrl
    mimeType
  }
  tireSet {
    name
  }
`;

/**
 * List query fields: core + minimal relations
 */
const EXPENSE_LIST_FIELDS = `
  ${EXPENSE_CORE_FIELDS}
  ${EXPENSE_LIST_RELATIONS}
`;

/**
 * Detail query fields: core + full relations
 */
const EXPENSE_DETAIL_FIELDS = `
  ${EXPENSE_CORE_FIELDS}
  ${EXPENSE_DETAIL_RELATIONS}
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const EXPENSE_LIST_QUERY = gql`
  query ExpenseList($filter: ExpenseFilter, $params: PaginationAndSorting) {
    expenseList(filter: $filter, params: $params) {
      code
      data {
        ${EXPENSE_LIST_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_GET_QUERY = gql`
  query ExpenseGet($id: ID) {
    expenseGet(id: $id) {
      code
      data {
        ${EXPENSE_DETAIL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_GET_MANY_QUERY = gql`
  query ExpenseGetMany($ids: [ID]) {
    expenseGetMany(ids: $ids) {
      code
      data {
        ${EXPENSE_DETAIL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const EXPENSE_CREATE_MUTATION = gql`
  mutation ExpenseCreate($params: ExpenseInput) {
    expenseCreate(params: $params) {
      code
      data {
        ${EXPENSE_DETAIL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_UPDATE_MUTATION = gql`
  mutation ExpenseUpdate($where: ExpenseWhereInput, $params: ExpenseInput) {
    expenseUpdate(where: $where, params: $params) {
      code
      data {
        ${EXPENSE_DETAIL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_REMOVE_MUTATION = gql`
  mutation ExpenseRemove($where: ExpenseWhereInput) {
    expenseRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_REMOVE_MANY_MUTATION = gql`
  mutation ExpenseRemoveMany($where: [ExpenseWhereInput]) {
    expenseRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface Expense {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  carId?: string | undefined;
  /**
   * Discriminator field:
   * 1 = Refuel
   * 2 = Expense
   * 3 = Checkpoint
   * 4 = Travel Point
   * 5 = Revenue
   */
  expenseType?: number | undefined;
  // Common fields
  // Odometer in car's mileageIn units
  odometer?: number | undefined;
  // Odometer in user preferences's distanceIn units
  odometerDisplay?: number | undefined;
  // Trip meter reading in car's mileageIn units - calculated field between 2 refuels/expenses
  tripMeter?: number | undefined;
  // Trip meter reading in preferences's distanceIn units - calculated field between 2 refuels/expenses
  tripMeterDisplay?: number | undefined;
  whenDone?: string | undefined;
  /** Location/address (legacy field - use structured address fields instead) */
  location?: string | undefined;
  whereDone?: string | undefined;
  subtotal?: number | undefined;
  tax?: number | undefined;
  fees?: number | undefined;
  /**
   * Total amount in payment currency.
   * For expenses/refuels: money spent (positive = cost)
   * For revenues: money earned (positive = income)
   */
  totalPrice?: number | undefined;
  paidInCurrency?: string | undefined;
  totalPriceInHc?: number | undefined;
  homeCurrency?: string | undefined;
  expensePictureId?: string | undefined;
  comments?: string | undefined;
  fuelInTank?: number | undefined;
  travelId?: string | undefined;
  ownerNumber?: number | undefined;
  /**
   * Kind ID - interpretation depends on expenseType:
   * - For expenseType=2 (Expense): references expense_kinds table
   * - For expenseType=5 (Revenue): references revenue_kinds table
   */
  kindId?: number | undefined;
  // Expense-specific fields (expenseType = 2)
  costWork?: number | undefined;
  costParts?: number | undefined;
  costWorkHc?: number | undefined;
  costPartsHc?: number | undefined;
  /** Brief note about the expense or revenue (expenseType=2,5) */
  shortNote?: string | undefined;
  // Refuel-specific fields (expenseType = 1)
  // Refuel volume in volumeEnteredIn units
  refuelVolume?: number | undefined;
  // Unit for volume in user's preferences
  refuelVolumeDisplay?: number | null | undefined;
  volumeEnteredIn?: string | undefined;
  pricePerVolume?: number | undefined;
  isFullTank?: boolean | undefined;
  remainingInTankBefore?: number | undefined;
  fuelGrade?: string | undefined;
  tankType?: string | undefined;
  consumption?: number | undefined;
  // Travel-specific fields
  pointType?: string | null | undefined;

  // Address fields
  /** Street address */
  address1?: string | undefined;
  /** PO Box, Apartment, Suite, etc. */
  address2?: string | undefined;
  /** City */
  city?: string | undefined;
  /** Postal Code/Zip */
  postalCode?: string | undefined;
  /** State or Province */
  stateProvince?: string | undefined;
  /** Country full name */
  country?: string | undefined;
  /** Country code: US, CA, FR, RU, etc. */
  countryId?: string | undefined;
  /** Reference to a saved place (kind of replacement for the address related fields, whereDone and location) used  */
  savedPlaceId?: string | null | undefined;

  // Coordinates
  /** Location longitude */
  longitude?: number | undefined;
  /** Location latitude */
  latitude?: number | undefined;

  // Weather data (auto-populated based on location and time)
  /** Temperature in Celsius */
  weatherTempC?: number | undefined;
  /** Feels like temperature (wind chill/heat index) in Celsius */
  weatherFeelsLikeC?: number | undefined;
  /** Weather condition code: clear, rain, snow, etc. */
  weatherConditionCode?: string | undefined;
  /** Weather provider icon code for UI display */
  weatherConditionIcon?: string | undefined;
  /** Human-readable weather description */
  weatherDescription?: string | undefined;
  /** Humidity percentage 0-100 */
  weatherHumidityPct?: number | undefined;
  /** Atmospheric pressure in hectopascals */
  weatherPressureHpa?: number | undefined;
  /** Cloud cover percentage 0-100 */
  weatherCloudPct?: number | undefined;
  /** Visibility in meters */
  weatherVisibilityM?: number | undefined;
  /** Wind speed in meters per second */
  weatherWindSpeedMps?: number | undefined;
  /** Wind direction in degrees 0-359 */
  weatherWindDirDeg?: number | undefined;
  /** Precipitation in mm (last hour) */
  weatherPrecipMm?: number | undefined;
  /** UV index */
  weatherUvIndex?: number | undefined;
  /** Weather data provider: openweathermap, weatherapi, etc. */
  weatherProvider?: string | undefined;
  /** When weather data was retrieved from provider */
  weatherFetchedAt?: string | undefined;

  /** Reference to a tire set associated with this expense */
  tireSetId?: string | undefined | null;

  // Audit fields
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  // References
  user?: User | undefined;
  car?: Car | undefined;
  tags?: ExpenseTag[] | undefined;
  travel?: Travel | undefined;
  /** Expense kind - populated when expenseType=2 */
  expenseKind?: ExpenseKind | undefined;
  /** Revenue kind - populated when expenseType=5 */
  revenueKind?: RevenueKind | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
  uploadedFilesIds?: string[] | undefined;
  uploadedFiles?: UploadedFile[] | undefined;
  tireSet?: TireSet | undefined | null;
}

export interface ExpenseInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  carId?: string | null | undefined;
  /**
   * Record type:
   * 1 = Refuel
   * 2 = Expense
   * 3 = Checkpoint
   * 4 = Travel Point
   * 5 = Revenue
   */
  expenseType?: number | null | undefined;
  // Common fields
  odometer?: number | null | undefined;
  whenDone?: string | null | undefined;
  location?: string | null | undefined;
  whereDone?: string | null | undefined;
  subtotal?: number | null | undefined;
  tax?: number | null | undefined;
  fees?: number | null | undefined;
  totalPrice?: number | null | undefined;
  paidInCurrency?: string | null | undefined;
  totalPriceInHc?: number | null | undefined;
  homeCurrency?: string | null | undefined;
  comments?: string | null | undefined;
  fuelInTank?: number | null | undefined;
  travelId?: string | null | undefined;
  /**
   * Kind ID:
   * - For expenseType=2: ID from expense_kinds
   * - For expenseType=5: ID from revenue_kinds
   */
  kindId?: number | null | undefined;
  // Expense-specific fields (expenseType = 2)
  costWork?: number | null | undefined;
  costParts?: number | null | undefined;
  costWorkHc?: number | null | undefined;
  costPartsHc?: number | null | undefined;
  /** Brief note (expenseType=2,5) */
  shortNote?: string | null | undefined;
  // Refuel-specific fields (expenseType = 1)
  refuelVolume?: number | null | undefined;
  volumeEnteredIn?: string | null | undefined;
  pricePerVolume?: number | null | undefined;
  isFullTank?: boolean | null | undefined;
  remainingInTankBefore?: number | null | undefined;
  fuelGrade?: string | null | undefined;
  tankType?: string | null | undefined;
  // Travel-specific fields
  pointType?: string | null | undefined;

  // Address fields
  /** Street address */
  address1?: string | null | undefined;
  /** PO Box, Apartment, Suite, etc. */
  address2?: string | null | undefined;
  /** City */
  city?: string | null | undefined;
  /** Postal Code/Zip */
  postalCode?: string | null | undefined;
  /** State or Province */
  stateProvince?: string | null | undefined;
  /** Country full name */
  country?: string | null | undefined;
  /** Country code: US, CA, FR, RU, etc. */
  countryId?: string | null | undefined;
  /** Reference to a saved place (kind of replacement for the address related fields, whereDone and location) used  */
  savedPlaceId?: string | null | undefined;

  // Coordinates
  /** Location longitude */
  longitude?: number | null | undefined;
  /** Location latitude */
  latitude?: number | null | undefined;

  /** Reference to a tire set associated with this expense */
  tireSetId?: string | undefined | null;

  // Note: Weather fields are NOT included in input - they are auto-populated
  // by the backend based on location coordinates and whenDone timestamp

  // Related entities input
  tags?: string[] | null | undefined;
  status?: number | null | undefined;
  uploadedFilesIds?: string[] | undefined;

  // Save the place. If true then use whereDone, location, address1, address2 and other address related fields to create a record in saved placed and use its reference
  savePlace?: boolean | null | undefined;
  // If true then try to lookup a saved place by coordinates and use its ID as savedPlaceId and fill out whereDone, location, address1 and other places
  lookupSavedPlaceByCoordinates?: boolean | null | undefined;
}

export interface ExpenseFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  userId?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  /**
   * Filter by expense type:
   * 1 = Refuel
   * 2 = Expense
   * 3 = Checkpoint
   * 4 = Travel Point
   * 5 = Revenue
   */
  expenseType?: number[] | null | undefined;
  /**
   * Filter by kind ID. Note: kindId meaning depends on expenseType.
   * Use with expenseType filter to ensure correct interpretation.
   */
  kindId?: number[] | null | undefined;
  travelId?: string[] | null | undefined;
  /** Filter by point type (expenseType=4 only): home, office, client, other */
  pointType?: string[] | null | undefined;
  isFullTank?: boolean | null | undefined;
  fuelGrade?: string[] | null | undefined;
  tankType?: string[] | null | undefined;
  whenDoneFrom?: string | null | undefined;
  whenDoneTo?: string | null | undefined;

  // Location filters
  /** Filter by city */
  city?: string[] | null | undefined;
  /** Filter by state/province */
  stateProvince?: string[] | null | undefined;
  /** Filter by country code (US, CA, FR, RU, etc.) */
  countryId?: string[] | null | undefined;

  // Weather condition filter
  /** Filter by weather condition code: clear, rain, snow, etc. */
  weatherConditionCode?: string[] | null | undefined;

  /** Filter by tag IDs */
  tags?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface ExpenseWhereInput {
  id?: string | undefined;
}

/**
 * Expense type constants for clearer code
 */
export const EXPENSE_TYPE = {
  REFUEL: 1,
  EXPENSE: 2,
  CHECKPOINT: 3,
  TRAVEL_POINT: 4,
  REVENUE: 5,
} as const;

export type ExpenseTypeValue = (typeof EXPENSE_TYPE)[keyof typeof EXPENSE_TYPE];

/**
 * Weather condition codes for filtering and display
 */
export const WEATHER_CONDITION = {
  CLEAR: 'clear',
  CLOUDS: 'clouds',
  RAIN: 'rain',
  DRIZZLE: 'drizzle',
  THUNDERSTORM: 'thunderstorm',
  SNOW: 'snow',
  MIST: 'mist',
  FOG: 'fog',
  HAZE: 'haze',
} as const;

export type WeatherConditionValue = (typeof WEATHER_CONDITION)[keyof typeof WEATHER_CONDITION];

// =============================================================================
// Service Class
// =============================================================================

/**
 * Expense service that handles all expense-related API calls.
 * This is a unified service for:
 * - Refuels (expenseType = 1)
 * - Expenses (expenseType = 2)
 * - Checkpoints (expenseType = 3)
 * - Travel Points (expenseType = 4)
 * - Revenues (expenseType = 5)
 *
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class ExpenseService {
  /**
   * Get a list of expenses with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ExpenseFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single expense by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple expenses by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new expense
   */
  static async create(params: ExpenseInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing expense
   */
  static async update(
    where: ExpenseWhereInput,
    params: ExpenseInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete an expense (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple expenses (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EXPENSE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}